import qs from 'query-string';
import {DefaultErrorHandler} from './DefaultErrorHandler';
import {defaultHttpClient} from './DefaultHttpClient';
import {ErrorHandler, HttpRequest, HttpService, HttpServiceResponse} from './http';
import {Dictionary} from '../common';

export interface HttpClient {
    get(url: string, params?: {}): Promise<any>;
    post(url: string, params?: {}): Promise<any>;
    put(url: string, params?: {}): Promise<any>;
    delete(url: string): Promise<any>;
    [propName: string]: any;
}

interface HttpClientParams {
    client: HttpClient;
    errorHandler: ErrorHandler;
}

export class DefaultHttpService implements HttpService {
    private client: HttpClient;
    private errorHandler: ErrorHandler;

    constructor(
        {client, errorHandler}: HttpClientParams = {
            client: defaultHttpClient,
            errorHandler: new DefaultErrorHandler(),
        },
    ) {
        this.client = client;
        this.errorHandler = errorHandler;
    }

    async get<T>(url: string, params?: any): Promise<HttpServiceResponse<T>> {
        const operation = () =>
            this.client.get(url, {
                params,
                paramsSerializer: (_params: Dictionary<any>) => qs.stringify(_params),
            });

        return this.httpOperationHandler(operation);
    }

    async post<T>(url: string, params?: any): Promise<HttpServiceResponse<T>> {
        const operation = () => this.client.post(url, params);

        return this.httpOperationHandler(operation);
    }

    async put<T>(url: string, params?: any): Promise<HttpServiceResponse<T>> {
        const operation = () => this.client.put(url, params);

        return this.httpOperationHandler(operation);
    }

    async delete<T>(url: string): Promise<HttpServiceResponse<T>> {
        const operation = () => this.client.delete(url);

        return this.httpOperationHandler<T>(operation);
    }

    async request<T>(request: HttpRequest): Promise<HttpServiceResponse<T>> {
        switch (request.method) {
            case 'GET':
                return this.get(request.url, request.params);
            case 'POST':
                return this.post(request.url, request.params);
            case 'PUT':
                return this.put(request.url, request.params);
            case 'DELETE':
                return this.delete(request.url);
            case 'HEAD':
            case 'PATCH':
            case 'OPTIONS':
            case 'TRACE':
                throw Error(`Unhandled method ${request.method}`);
        }
    }

    private async httpOperationHandler<T>(operation: () => Promise<any>): Promise<HttpServiceResponse<T>> {
        let response;
        let error = null;

        try {
            response = await operation();
        } catch (err) {
            this.errorHandler.handle(err);
            error = err;
            response = {};
        }

        return [response.data, error, response];
    }
}
